import {
  type CartEntry,
  groupByName,
  isOnCart,
  STORAGE_LABEL,
  type StoragePlace,
  storageLabel,
} from "@larder/shared";
import { useMemo, useState } from "react";
import { CartRow } from "../components/CartRow";
import { CheckIn } from "../components/CheckIn";
import { ItemRow } from "../components/ItemRow";
import { ShowMore } from "../components/ShowMore";
import type { TrackedItem } from "../data/items";
import { FIRST_PAGE, page } from "../data/paging";
import type { LarderState } from "../data/useLarder";
import { useLeaving } from "../data/useLeaving";

/**
 * How many eaten things the home screen shows before it stops.
 *
 * Enough to answer "where did it go?" — which is the entire job of the section —
 * without turning the home screen into an archive. The calendar is where the
 * long history lives.
 */
const CONSUMED_SHOWN = 5;

/**
 * The home screen.
 *
 * Ordered by what the user has to DO, not by what the data model contains:
 * questions that need answering come first, then what to buy, then what is in
 * the house, then what is already settled. Someone with nothing to answer should
 * be able to close the app in two seconds and feel finished — that is the
 * one-minute-a-day promise.
 *
 * Every state an item can be in now has somewhere to appear. It used to be that
 * answering "ate it" dropped an item out of every bucket, so a user who did the
 * thing the app asked for watched it vanish and reasonably concluded it had been
 * deleted. Showing the win is not decoration; it is the difference between a
 * tracker and a shredder.
 *
 * The search bar searches YOUR LARDER, not a catalogue. "Do I already have
 * jeera?" is the question people actually ask a kitchen app, and answering it
 * from the top of the screen is what prevents a duplicate purchase. Adding
 * moved to the floating button, because you search far more often than you add.
 */
export function Larder({
  state,
  places,
  cart,
  onAnswer,
  onOpen,
  onBuy,
  onOpenCart,
  onAddToCart,
  busy,
  banner,
}: {
  state: LarderState;
  places: StoragePlace[];
  cart: CartEntry[];
  /** An app-level promo that scrolls with the list. Currently the install nudge. */
  banner?: React.ReactNode;
  onOpen: (id: string) => void;
  onAnswer: (itemId: string, type: "ate" | "tossed" | "still_here") => void;
  /** Bought it — opens the add flow prefilled from the entry. */
  onBuy: (cartId: string) => void;
  /**
   * Opening the line: where to buy it, what it cost last time, and the way to
   * take it off the list. Removal lives in there rather than on the row — see
   * CartRow.
   */
  onOpenCart: (cartId: string) => void;
  onAddToCart: (name: string) => void;
  busy: boolean;
}) {
  const { leaving, dismiss } = useLeaving();
  const [q, setQ] = useState("");
  /**
   * How much of each long bucket is on screen.
   *
   * Only the two that grow without limit. "Still got these?" is deliberately
   * uncapped — it is the work, and hiding work behind a button is how a to-do
   * list starts lying to you.
   */
  const [activeCap, setActiveCap] = useState(FIRST_PAGE);
  const [undatedCap, setUndatedCap] = useState(FIRST_PAGE);

  const term = q.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!term) return null;
    // Brand and size are searchable too: people look for "amul" as often as
    // they look for "milk".
    const hay = (i: TrackedItem) =>
      `${i.name} ${i.brand ?? ""} ${i.sizeText ?? ""}`.toLowerCase();
    return state.items.filter((i) => hay(i).includes(term));
  }, [state.items, term]);

  /** "In the house", grouped then capped. Rows, not items — see the note below. */
  const activePage = useMemo(() => {
    const rows = groupByName(state.active);
    return { rows, paging: page(rows.length, activeCap) };
  }, [state.active, activeCap]);

  /**
   * "Doesn't go off", capped ACROSS its place groups.
   *
   * The cap is a budget spent walking the places in order, so the first shelf
   * fills up before the second starts — capping each place separately would
   * show eight rows from every one of them and cap nothing.
   */
  const undatedPage = useMemo(() => {
    const all = byPlace(state.undated, places).map(
      ([place, group]) => [place, groupByName(group)] as const,
    );
    const total = all.reduce((n, [, rows]) => n + rows.length, 0);
    const paging = page(total, undatedCap);

    const groups: typeof all = [];
    let budget = paging.visible;
    for (const [place, rows] of all) {
      if (budget <= 0) break;
      groups.push([place, rows.slice(0, budget)] as const);
      budget -= Math.min(budget, rows.length);
    }
    return { groups, paging, manyPlaces: all.length > 1 };
  }, [state.undated, places, undatedCap]);

  // A cart with things on it is not an empty larder — you have started, you
  // just haven't bought anything yet.
  const empty = state.status === "ready" && state.items.length === 0 && cart.length === 0;
  const loading = state.status === "loading";

  return (
    <>
      <div className="quickadd">
        <span className="mag" aria-hidden="true">
          🔎
        </span>
        <input
          className="searchfield"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your larder…"
          aria-label="Search your larder"
          enterKeyHint="search"
          data-testid="search"
        />
        {q && (
          <button
            type="button"
            className="clearsearch"
            onClick={() => setQ("")}
            aria-label="Clear search"
            data-testid="clear-search"
          >
            ✕
          </button>
        )}
      </div>

      <section aria-label="larder" className="scroll">
        {/* Owned by App, rendered here: it belongs INSIDE the scroller so it
            scrolls away like content instead of pinning itself over the list. */}
        {banner}
        {loading && (
          <div role="status" aria-label="Loading your larder">
            <div className="skeleton wide" />
            <div className="skeleton" />
            <div className="skeleton" />
          </div>
        )}
        {state.status === "error" && (
          <p className="hint" data-testid="larder-error">
            {state.error}
          </p>
        )}

        {matches ? (
          <div className="bucket" data-testid="search-results">
            <h3>
              {matches.length} match{matches.length === 1 ? "" : "es"}
            </h3>
            {matches.map((item, i) => (
              <ItemRow
                key={item.id}
                item={item}
                places={places}
                index={i}
                onClick={() => onOpen(item.id)}
              />
            ))}
            {matches.length === 0 && (
              <div className="empty" data-testid="no-matches">
                <p>
                  <strong>Nothing called “{q.trim()}”.</strong>
                </p>
                {/* The sentence was already here and already right. It was one
                    button short of being useful: the moment you learn you have
                    run out is the moment to write it down. */}
                <p className="dim">So you probably need to buy it.</p>
                {isOnCart(q.trim(), cart) ? (
                  <p className="dim" data-testid="already-carted">
                    Already on your shopping list. 🛒
                  </p>
                ) : (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => {
                      onAddToCart(q.trim());
                      setQ("");
                    }}
                    data-testid="add-to-cart"
                  >
                    🛒 Add “{q.trim()}” to shopping list
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {state.needsAnswer.length > 0 && (
              <div className="bucket" data-testid="bucket-answer">
                <h3>
                  Still got these?{" "}
                  <span className="count">{state.needsAnswer.length}</span>
                </h3>
                {state.needsAnswer.map((item, i) => (
                  <CheckIn
                    key={item.id}
                    item={item}
                    index={i}
                    busy={busy}
                    leaving={leaving.has(item.id)}
                    onAnswer={(type) => dismiss(item.id, () => onAnswer(item.id, type))}
                  />
                ))}
              </div>
            )}

            {/* Second, not first: something you have to answer beats something
                you have to buy, because only one of them is spoiling. */}
            {cart.length > 0 && (
              <div className="bucket" data-testid="bucket-cart">
                <h3>
                  Shopping list <span className="count">{cart.length}</span>
                </h3>
                {/* "Tick", not "tap": the row now has two halves, and the tap
                    that says you have bought it is the circle. */}
                <p className="note">
                  Things you've run out of. Tick one when you've bought it.
                </p>
                {cart.map((entry, i) => (
                  <CartRow
                    key={entry.id}
                    entry={entry}
                    index={i}
                    busy={busy}
                    onBuy={() => onBuy(entry.id)}
                    onOpen={() => onOpenCart(entry.id)}
                  />
                ))}
              </div>
            )}

            {state.active.length > 0 && (
              <div className="bucket" data-testid="bucket-active">
                <h3>
                  In the house <span className="count">{state.active.length}</span>
                </h3>
                {/* Grouped: two cartons of the same milk are one line with a
                    count, not two lines that look like a mistake. The soonest
                    leads, because that is the one to use first.

                    Paged AFTER grouping, so the cap counts rows the user can
                    see rather than items in the database — capping first would
                    show eight items as five lines and call it eight. */}
                {activePage.rows.slice(0, activePage.paging.visible).map((g, i) => (
                  <ItemRow
                    key={g.members[0]?.id ?? g.key}
                    item={g.members[0] as TrackedItem}
                    places={places}
                    index={i}
                    count={g.members.length}
                    onClick={() => onOpen(g.members[0]?.id ?? "")}
                  />
                ))}
                <ShowMore
                  label={activePage.paging.label}
                  hidden={activePage.paging.hidden}
                  onClick={() => setActiveCap(activePage.paging.next)}
                  testid="more-active"
                />
              </div>
            )}

            {state.undated.length > 0 && (
              <div className="bucket" data-testid="bucket-undated">
                <h3>
                  Doesn't go off <span className="count">{state.undated.length}</span>
                </h3>
                {/* Grouped by PLACE, not by date — for a book or a spare bulb,
                    "which shelf is it on" is the entire question. */}
                {undatedPage.groups.map(([place, rows]) => (
                  <div key={place} className="place-group">
                    {undatedPage.manyPlaces && <p className="place-heading">{place}</p>}
                    {rows.map((g, i) => (
                      <ItemRow
                        key={g.members[0]?.id ?? g.key}
                        item={g.members[0] as TrackedItem}
                        places={places}
                        index={i}
                        count={g.members.length}
                        onClick={() => onOpen(g.members[0]?.id ?? "")}
                      />
                    ))}
                  </div>
                ))}
                <ShowMore
                  label={undatedPage.paging.label}
                  hidden={undatedPage.paging.hidden}
                  onClick={() => setUndatedCap(undatedPage.paging.next)}
                  testid="more-undated"
                />
              </div>
            )}

            {/* The app's own GUESSES, with the same three buttons as a live
                check-in. There is no Binned tab any more: confirmed waste needs
                no screen, because you already told us and the event log has it.
                A presumption does — being able to say "actually I ate that" is
                what keeps the waste figure honest, and it belongs here rather
                than one tab away, where nobody went. */}
            {state.presumed.length > 0 && (
              <div className="bucket" data-testid="bucket-presumed">
                <h3>
                  We assumed these went off{" "}
                  <span className="count">{state.presumed.length}</span>
                </h3>
                <p className="bucket-note">
                  Nobody answered for these, so we guessed. Put us right if we got it
                  wrong.
                </p>
                {state.presumed.map((item) => (
                  <CheckIn
                    key={item.id}
                    item={item}
                    busy={busy}
                    leaving={leaving.has(item.id)}
                    onAnswer={(type) => dismiss(item.id, () => onAnswer(item.id, type))}
                  />
                ))}
              </div>
            )}

            {/* The answer to "where did it go?". An item answered "ate it" used
                to fall out of every bucket and look deleted. */}
            {state.consumed.length > 0 && (
              <div className="bucket" data-testid="bucket-consumed">
                <h3>
                  Consumed <span className="count">{state.consumed.length}</span>
                </h3>
                {state.consumed.slice(0, CONSUMED_SHOWN).map((item, i) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    places={places}
                    today={state.today}
                    index={i}
                    onClick={() => onOpen(item.id)}
                  />
                ))}
                {state.consumed.length > CONSUMED_SHOWN && (
                  <p className="note">
                    and {state.consumed.length - CONSUMED_SHOWN} more before that — the
                    calendar has the full history.
                  </p>
                )}
              </div>
            )}

            {empty && (
              <div className="empty" data-testid="empty-items">
                <p>
                  <strong>Nothing tracked yet.</strong>
                </p>
                <p className="dim">
                  Tap ＋ and add the one thing most likely to go off this week. That's the
                  whole habit.
                </p>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

/**
 * Buckets undated items by where they live.
 *
 * For anything that never expires the useful axis is location, not time. A
 * library is the clearest case: you do not want a flat list of two hundred
 * books, you want "Top shelf, left".
 */
function byPlace(
  items: TrackedItem[],
  places: StoragePlace[],
): Array<[string, TrackedItem[]]> {
  const map = new Map<string, TrackedItem[]>();
  for (const item of items) {
    const label = storageLabel(item.storageId, places, item.storage, STORAGE_LABEL);
    const list = map.get(label);
    if (list) list.push(item);
    else map.set(label, [item]);
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}
